import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Camera } from 'lucide-react';
import './CommonStyles.css';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';


const AddPaymentModal = ({ sale, invoice, onClose, onSuccess }) => {
    // State for payment form
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paidOn, setPaidOn] = useState(() => {
        return dateUtils.getChicagoISOString();
    });
    const [amount, setAmount] = useState('');
    const [paymentAccount, setPaymentAccount] = useState('');
    const [paymentNote, setPaymentNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Card payment fields
    const [cardNumber, setCardNumber] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [cardType, setCardType] = useState('visa');
    const [cardMonth, setCardMonth] = useState('');
    const [cardYear, setCardYear] = useState('');
    const [cardSecurity, setCardSecurity] = useState('');
    const [cardTransactionNumber, setCardTransactionNumber] = useState('');
    
    // Cheque payment fields
    const [chequeNumber, setChequeNumber] = useState('');
    const [chequeExpiryDate, setChequeExpiryDate] = useState('');
    const [bankSubmissionDate, setBankSubmissionDate] = useState('');
    
    // Bank transfer fields
    const [bankAccountNumber, setBankAccountNumber] = useState('');

    // Extract sale/invoice data
    const transactionId = sale?.id || sale?.apiData?.id;
    const customerName = sale?.customer || sale?.contact?.name || '--';
    const invoiceNo = sale?.invoice_no || '--';
    const locationName = sale?.location?.name || '--';
    const totalAmount = sale?.final_total || sale?.total_payable || '0.00';
    const locationId = sale?.location_id;
    
    console.log('=== AddPaymentModal Loaded ===');
    console.log('Sale data:', sale);
    console.log('Transaction ID:', transactionId);
    
    // Handle payment submission
    const handleAddPayment = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Validate required fields
            if (!transactionId) {
                setError('Transaction ID is required');
                setLoading(false);
                return;
            }
            
            if (!amount || parseFloat(amount) <= 0) {
                setError('Please enter a valid payment amount');
                setLoading(false);
                return;
            }
            
            // Format paid_on date to "Y-m-d H:i:s" format
            const formatDateTimeLocally = (dateTimeStr) => {
                if (!dateTimeStr) return null;
                // Since dateTimeStr comes from input type="datetime-local", it's in the browser's view of Chicago time.
                // We just need to replace T with space and maybe add seconds.
                return dateTimeStr.replace('T', ' ') + ':00';
            };
            
            // Build payment data matching the API examples
            const paymentData = {
                transaction_id: parseInt(transactionId),
                payment_method: paymentMethod,
                amount: parseFloat(amount),
                paid_on: formatDateTimeLocally(paidOn),
                payment_account_id: paymentAccount || paymentMethod,
                payment_note: paymentNote || null
            };
            
            // Add card-specific fields
            if (paymentMethod === 'card') {
                if (!cardNumber || !cardHolderName || !cardType) {
                    setError('Card number, holder name, and card type are required for card payments');
                    setLoading(false);
                    return;
                }
                paymentData.card_number = cardNumber;
                paymentData.card_holder_name = cardHolderName;
                paymentData.card_type = cardType;
                if (cardMonth) paymentData.card_month = cardMonth;
                if (cardYear) paymentData.card_year = cardYear;
                if (cardSecurity) paymentData.card_security = cardSecurity;
                if (cardTransactionNumber) paymentData.card_transaction_number = cardTransactionNumber;
            }
            
            // Add cheque-specific fields
            if (paymentMethod === 'cheque') {
                if (chequeNumber) paymentData.cheque_number = chequeNumber;
                if (chequeExpiryDate) paymentData.cheque_expiry_date = chequeExpiryDate;
                if (bankSubmissionDate) paymentData.bank_submission_date = bankSubmissionDate;
            }
            
            // Add bank transfer fields
            if (paymentMethod === 'bank_transfer') {
                if (!bankAccountNumber) {
                    setError('Bank account number is required for bank transfers');
                    setLoading(false);
                    return;
                }
                paymentData.bank_account_number = bankAccountNumber;
            }
            
            console.log('Submitting payment:', JSON.stringify(paymentData, null, 2));
            
            // Call API to add payment
            const response = await apiService.addPayment(paymentData);
            
            console.log('Payment added successfully:', response);
            alert('Payment added successfully!');
            
            setLoading(false);
            
            // Call success callback if provided
            if (onSuccess) {
                onSuccess(response);
            }
            
            // Close modal
            onClose();
            
        } catch (err) {
            console.error('Error adding payment:', err);
            setError(err.message || 'Failed to add payment');
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '2rem' }}>
            <div style={{ background: '#fff', width: '100%', maxWidth: '900px', borderRadius: '4px', display: 'flex', flexDirection: 'column', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', maxHeight: '90vh' }}>

                {/* Header */}
                <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#4b5563', fontWeight: '500' }}>
                        Add Payment
                    </h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', padding: '0.2rem' }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body Content */}
                <div style={{ padding: '1.5rem', overflowY: 'auto' }}>

                    {/* Error Message */}
                    {error && (
                        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.875rem' }}>
                            {error}
                        </div>
                    )}

                    {/* Top Stats Row (3 Grey Boxes) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>

                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '4px', border: '1px solid #e2e8f0', color: '#334155', fontSize: '0.875rem', lineHeight: '1.6' }}>
                            <div><strong>Customer:</strong> {customerName}</div>
                            <div><strong>Business:</strong> </div>
                        </div>

                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '4px', border: '1px solid #e2e8f0', color: '#334155', fontSize: '0.875rem', lineHeight: '1.6' }}>
                            <div><strong>Invoice No.:</strong> {invoiceNo}</div>
                            <div><strong>Location:</strong> {locationName}</div>
                        </div>

                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '4px', border: '1px solid #e2e8f0', color: '#334155', fontSize: '0.875rem', lineHeight: '1.6' }}>
                            <div><strong>Total amount:</strong> $ {totalAmount}</div>
                            <div><strong>Payment Note:</strong> {paymentNote || '--'}</div>
                        </div>

                    </div>

                    {/* Advance Balance */}
                    <div style={{ fontWeight: '700', color: '#111827', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Advance Balance: <span style={{ fontWeight: '400', color: '#4b5563' }}>$ 0.00</span>
                    </div>

                    {/* Form Fields - Row 1 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) minmax(200px, 1fr) minmax(200px, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>

                        {/* paymentMethod */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827' }}>Payment Method:*</label>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden', background: '#fff' }}>
                                <div style={{ padding: '0.5rem', background: '#f9fafb', borderRight: '1px solid #d1d5db', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DollarSign size={16} />
                                </div>
                                <select
                                    value={paymentMethod}
                                    onChange={e => setPaymentMethod(e.target.value)}
                                    style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', color: '#334155' }}
                                    disabled={loading}
                                >
                                    <option value="cash">Cash</option>
                                    <option value="card">Card</option>
                                    <option value="cheque">Cheque</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="credit">Credit</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* Paid on */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827' }}>Paid on:*</label>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden', background: '#f3f4f6' }}>
                                <div style={{ padding: '0.5rem', background: '#fff', borderRight: '1px solid #d1d5db', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Calendar size={16} />
                                </div>
                                <input
                                    type="datetime-local"
                                    value={paidOn}
                                    onChange={e => setPaidOn(e.target.value)}
                                    style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', color: '#334155' }}
                                />
                            </div>
                        </div>

                        {/* Amount */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827' }}>Amount:*</label>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden', background: '#fff' }}>
                                <div style={{ padding: '0.5rem', background: '#f9fafb', borderRight: '1px solid #d1d5db', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DollarSign size={16} />
                                </div>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', color: '#334155' }}
                                />
                            </div>
                        </div>

                    </div>

                    {/* Form Fields - Row 2 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>

                        {/* Payment Account */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827' }}>Payment Account:</label>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden', background: '#fff' }}>
                                <div style={{ padding: '0.5rem', background: '#f9fafb', borderRight: '1px solid #d1d5db', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DollarSign size={16} />
                                </div>
                                <select
                                    value={paymentAccount}
                                    onChange={e => setPaymentAccount(e.target.value)}
                                    style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', color: '#334155' }}
                                    disabled={loading}
                                >
                                    <option value="">None</option>
                                </select>
                            </div>
                        </div>

                        {/* Attach Document */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827' }}>Attach Document:</label>
                            <div>
                                <input type="file" id="payment-doc" style={{ display: 'none' }} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#4b5563' }}>
                                    <label htmlFor="payment-doc" style={{ background: '#f3f4f6', border: '1px solid #d1d5db', padding: '0.2rem 0.5rem', borderRadius: '3px', cursor: 'pointer' }}>
                                        Choose File
                                    </label>
                                    <span>No file chosen</span>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                    Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Payment Note */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Payment Note:</label>
                        <textarea
                            value={paymentNote}
                            onChange={e => setPaymentNote(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', color: '#334155', minHeight: '60px', resize: 'vertical' }}
                            placeholder="Enter payment note..."
                        />
                    </div>

                    {/* Card Payment Fields */}
                    {paymentMethod === 'card' && (
                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '4px', border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                            <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#111827', fontWeight: '600' }}>Card Payment Details</h4>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Card Number:*</label>
                                    <input
                                        type="text"
                                        value={cardNumber}
                                        onChange={e => setCardNumber(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        placeholder="1234 5678 9012 3456"
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Card Holder Name:*</label>
                                    <input
                                        type="text"
                                        value={cardHolderName}
                                        onChange={e => setCardHolderName(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        placeholder="John Doe"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Card Type:*</label>
                                    <select
                                        value={cardType}
                                        onChange={e => setCardType(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                    >
                                        <option value="visa">Visa</option>
                                        <option value="mastercard">Mastercard</option>
                                        <option value="amex">American Express</option>
                                        <option value="discover">Discover</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Expiry Month:</label>
                                    <input
                                        type="text"
                                        value={cardMonth}
                                        onChange={e => setCardMonth(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        placeholder="MM"
                                        maxLength="2"
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Expiry Year:</label>
                                    <input
                                        type="text"
                                        value={cardYear}
                                        onChange={e => setCardYear(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        placeholder="YYYY"
                                        maxLength="4"
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>CVV:</label>
                                    <input
                                        type="text"
                                        value={cardSecurity}
                                        onChange={e => setCardSecurity(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        placeholder="123"
                                        maxLength="4"
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: '1rem' }}>
                                <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Transaction Number:</label>
                                <input
                                    type="text"
                                    value={cardTransactionNumber}
                                    onChange={e => setCardTransactionNumber(e.target.value)}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                    placeholder="Transaction reference number"
                                />
                            </div>
                        </div>
                    )}

                    {/* Cheque Payment Fields */}
                    {paymentMethod === 'cheque' && (
                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '4px', border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                            <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#111827', fontWeight: '600' }}>Cheque Payment Details</h4>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Cheque Number:</label>
                                    <input
                                        type="text"
                                        value={chequeNumber}
                                        onChange={e => setChequeNumber(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        placeholder="CHQ123456"
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Cheque Expiry Date:</label>
                                    <input
                                        type="date"
                                        value={chequeExpiryDate}
                                        onChange={e => setChequeExpiryDate(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Bank Submission Date:</label>
                                    <input
                                        type="date"
                                        value={bankSubmissionDate}
                                        onChange={e => setBankSubmissionDate(e.target.value)}
                                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bank Transfer Fields */}
                    {paymentMethod === 'bank_transfer' && (
                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '4px', border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                            <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#111827', fontWeight: '600' }}>Bank Transfer Details</h4>
                            
                            <div>
                                <label style={{ fontWeight: '700', fontSize: '0.85rem', color: '#111827', display: 'block', marginBottom: '0.4rem' }}>Bank Account Number:*</label>
                                <input
                                    type="text"
                                    value={bankAccountNumber}
                                    onChange={e => setBankAccountNumber(e.target.value)}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                    placeholder="Enter bank account number"
                                />
                            </div>
                        </div>
                    )}

                    {/* Left Add Payment Button (Green) */}
                    <div>
                        <button 
                            onClick={handleAddPayment}
                            disabled={loading}
                            style={{ 
                                background: loading ? '#9ca3af' : '#10b981', 
                                color: '#fff', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '0.5rem 1rem', 
                                fontSize: '0.85rem', 
                                fontWeight: '600', 
                                cursor: loading ? 'not-allowed' : 'pointer' 
                            }}
                        >
                            {loading ? 'Processing...' : 'Add Payment'}
                        </button>
                    </div>

                </div>

                {/* Footer Center Aligned as requested */}
                <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: '#fff', borderRadius: '0 0 4px 4px' }}>
                    <button style={{ background: '#581c87', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Save
                    </button>
                    <button onClick={onClose} style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddPaymentModal;
